import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const TicketReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [ticketData, setTicketData] = useState<any>(null);

  useEffect(() => {
    fetchTicketData();
  }, []);

  const fetchTicketData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets');
      const tickets = response.data.data || [];
      
      // Calculate ticket statistics
      const stats = calculateTicketStats(tickets);
      setTicketData(stats);
    } catch (error) {
      console.error('Failed to fetch ticket data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTicketStats = (tickets: any[]) => {
    // Status distribution
    const statusData = [
      { name: 'Open', value: tickets.filter(t => t.status === 'OPEN').length, fill: '#ffc107' },
      { name: 'In Progress', value: tickets.filter(t => t.status === 'IN_PROGRESS').length, fill: '#0dcaf0' },
      { name: 'Pending', value: tickets.filter(t => t.status === 'PENDING_CUSTOMER').length, fill: '#6c757d' },
      { name: 'Resolved', value: tickets.filter(t => t.status === 'RESOLVED').length, fill: '#198754' },
      { name: 'Closed', value: tickets.filter(t => t.status === 'CLOSED').length, fill: '#212529' },
    ].filter(item => item.value > 0);

    // Priority distribution
    const priorityData = [
      { name: 'Low', value: tickets.filter(t => t.priority === 'Low').length, fill: '#198754' },
      { name: 'Medium', value: tickets.filter(t => t.priority === 'Medium').length, fill: '#ffc107' },
      { name: 'High', value: tickets.filter(t => t.priority === 'High').length, fill: '#fd7e14' },
      { name: 'Urgent', value: tickets.filter(t => t.priority === 'Urgent').length, fill: '#dc3545' },
    ].filter(item => item.value > 0);

    // Category distribution
    const categoryMap = new Map();
    tickets.forEach(t => {
      const category = t.category || 'Uncategorized';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });
    const categoryData = Array.from(categoryMap.entries())
      .map(([name, value], index) => ({ name, value, fill: COLORS[index % COLORS.length] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    // Tickets over time (last 7 days)
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const count = tickets.filter(t => {
        const ticketDate = new Date(t.createdAt);
        return ticketDate.toDateString() === date.toDateString();
      }).length;
      last7Days.push({ date: dateStr, tickets: count });
    }

    return {
      statusData,
      priorityData,
      categoryData,
      timelineData: last7Days,
      totalTickets: tickets.length,
    };
  };

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-1">
          <i className="fas fa-file-alt me-2 text-primary"></i>
          Ticket Reports
        </h2>
        <p className="text-muted mb-0 small">Comprehensive ticket statistics and analysis</p>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-primary rounded-circle p-3 me-3">
                  <i className="fas fa-ticket-alt text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">Total Tickets</h6>
                  <h3 className="mb-0">{ticketData?.totalTickets || 0}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-warning rounded-circle p-3 me-3">
                  <i className="fas fa-exclamation-circle text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">Open Tickets</h6>
                  <h3 className="mb-0">{ticketData?.statusData.find((s: any) => s.name === 'Open')?.value || 0}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-info rounded-circle p-3 me-3">
                  <i className="fas fa-tasks text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">In Progress</h6>
                  <h3 className="mb-0">{ticketData?.statusData.find((s: any) => s.name === 'In Progress')?.value || 0}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-success rounded-circle p-3 me-3">
                  <i className="fas fa-check-circle text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">Resolved</h6>
                  <h3 className="mb-0">{ticketData?.statusData.find((s: any) => s.name === 'Resolved')?.value || 0}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row>
        {/* Status Distribution */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-pie me-2 text-primary"></i>
                Status Distribution
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {ticketData?.statusData && ticketData.statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={ticketData.statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {ticketData.statusData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">No data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Priority Distribution */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Priority Distribution
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {ticketData?.priorityData && ticketData.priorityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={ticketData.priorityData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {ticketData.priorityData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">No data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Tickets Over Time */}
        <Col lg={12} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-line me-2 text-info"></i>
                Ticket Trends (Last 7 Days)
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {ticketData?.timelineData && (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={ticketData.timelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="tickets" stroke="#0dcaf0" strokeWidth={3} dot={{ fill: '#0dcaf0', r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Category Distribution */}
        <Col lg={12} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-tags me-2 text-success"></i>
                Top Categories
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {ticketData?.categoryData && ticketData.categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ticketData.categoryData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#198754">
                      {ticketData.categoryData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">No data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};



